import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

import styles from "../../Styles";
import HTMLContent from "../HTMLContent";
import { KeyValueTable, SectionTitle, SubSectionTitle } from "../shared";
import { formatDisplayValue } from "../../helpers";
import { PageReference } from "../PageReference";
import { TOC_ANCHORS } from "../../helpers/tableOfContents";

const formatDate = (dateUtils, value) => {
    if (!value) {
        return "-";
    }

    return dateUtils.convertDateToDate(value, INTERNATIONAL_DATE_FORMAT);
};

const toYesNo = (value) => {
    if (value === true) {
        return "Yes";
    }
    if (value === false) {
        return "No";
    }
    return "-";
};

const resolveShareholderName = (shareholder = {}) => {
    if (shareholder?.companyName) {
        return shareholder.companyName;
    }

    return [
        shareholder?.title,
        shareholder?.firstName,
        shareholder?.lastName,
    ].filter(Boolean).join(" ");
};

const resolveShareholderType = (shareholder = {}) => {
    if (shareholder?.companyName) {
        return "Company";
    }

    if (shareholder?.firstName || shareholder?.lastName) {
        return "Individual";
    }

    return "-";
};

const buildLocationRows = (locations = []) => {
    if (!Array.isArray(locations) || !locations.length) {
        return [{ title: "No Data", details: "-" }];
    }

    return locations.flatMap((location, index) => {
        const prefix = `#${index + 1}`;

        return [
            { title: `${prefix} - Location Type`, details: location?.type },
            { title: `${prefix} - Street Address`, details: location?.address },
            { title: `${prefix} - Postcode`, details: location?.postalCode },
            { title: `${prefix} - City`, details: location?.city },
            { title: `${prefix} - Region/State`, details: location?.regionName || location?.state },
            { title: `${prefix} - Region Code`, details: location?.regionCode },
            { title: `${prefix} - Region Short`, details: location?.regionShort },
            { title: `${prefix} - Country`, details: location?.countryName },
            { title: `${prefix} - ISO Country Code`, details: location?.countryCode },
            { title: `${prefix} - Deliverability Status`, details: location?.deliverabilityStatus },
        ];
    });
};

const buildContactRows = (contactPersons = []) => {
    if (!Array.isArray(contactPersons) || !contactPersons.length) {
        return [{ title: "No Data", details: "-" }];
    }

    return contactPersons.flatMap((contact, index) => {
        const prefix = `#${index + 1}`;

        return [
            {
                title: `${prefix} - Name`,
                details: [contact?.title, contact?.name, contact?.lastName].filter(Boolean).join(" "),
            },
            { title: `${prefix} - Email`, details: contact?.email },
            { title: `${prefix} - Phone Number`, details: contact?.phoneNumber },
            { title: `${prefix} - Fax Number`, details: contact?.faxNumber },
            { title: `${prefix} - Country Calling Code`, details: contact?.countryCallingCode },
            { title: `${prefix} - Position`, details: contact?.position },
        ];
    });
};

const buildShareholderRows = (shareHolders = {}) => {
    const companyShareholders = Array.isArray(shareHolders?.company) ? shareHolders.company : [];
    const individualShareholders = Array.isArray(shareHolders?.individual) ? shareHolders.individual : [];
    const allShareholders = [
        ...companyShareholders.map((shareholder) => ({ ...shareholder, shareholderType: "Company" })),
        ...individualShareholders.map((shareholder) => ({ ...shareholder, shareholderType: "Individual" })),
    ];

    if (!allShareholders.length) {
        return [{ title: "No Data", details: "-" }];
    }

    return allShareholders.flatMap((shareholder, index) => {
        const prefix = `#${index + 1}`;

        return [
            { title: `${prefix} - Shareholder Name`, details: resolveShareholderName(shareholder) },
            {
                title: `${prefix} - Shareholder Address`,
                details: shareholder?.address || shareholder?.shareholderAddress,
            },
            {
                title: `${prefix} - Company Type`,
                details: shareholder?.shareholderType || resolveShareholderType(shareholder),
            },
            { title: `${prefix} - Share Percent`, details: shareholder?.percentage },
            { title: `${prefix} - DUNS Number`, details: shareholder?.dunsNumber },
            { title: `${prefix} - UBO`, details: toYesNo(shareholder?.isUbo) },
            {
                title: `${prefix} - Country / Region / City`,
                details: [
                    shareholder?.country?.name || shareholder?.country,
                    shareholder?.region,
                    shareholder?.city,
                    shareholder?.residenceCountry?.name,
                ].filter(Boolean).join(", "),
            },
        ];
    });
};

const buildManagementRows = (teamManagement = [], dateUtils) => {
    if (!Array.isArray(teamManagement) || !teamManagement.length) {
        return [{ title: "No Data", details: "-" }];
    }

    return teamManagement.flatMap((manager, index) => {
        const prefix = `#${index + 1}`;

        return [
            {
                title: `${prefix} - Full Name`,
                details: [
                    manager?.title,
                    manager?.fullName || manager?.name,
                    manager?.lastName,
                ].filter(Boolean).join(" "),
            },
            { title: `${prefix} - Position`, details: manager?.position },
            { title: `${prefix} - Email`, details: manager?.email },
            { title: `${prefix} - Phone Number`, details: manager?.phoneNumber },
            { title: `${prefix} - Sanction Status`, details: manager?.sanctionStatus },
            { title: `${prefix} - PEPs Status`, details: manager?.pepStatus || manager?.pepsStatus },
            {
                title: `${prefix} - Active`,
                details: toYesNo(manager?.active ?? manager?.isActive),
            },
            {
                title: `${prefix} - Appointment Date`,
                details: formatDate(dateUtils, manager?.appointmentDate || manager?.createdAt),
            },
        ];
    });
};

const mapRows = (rows) => rows.map((row) => ({
    ...row,
    details: formatDisplayValue(row.details),
}));

const BasicCompanyInformation = ({ t, supplierData = {} }) => {
    const dateUtils = new DateUtils();
    const supplierDetail = supplierData?.supplierDetail || {};
    const industries = Array.isArray(supplierDetail?.industries)
        ? supplierDetail.industries
            .map((industry) => [industry?.class, industry?.name].filter(Boolean).join(" - "))
            .filter(Boolean)
            .join(", ")
        : "-";

    const basicInfoRows = [
        { title: "Company Name", details: supplierDetail?.name },
        {
            title: "Former Company Name",
            details: supplierDetail?.formerCompanyName || supplierDetail?.localLanguageFullName,
        },
        { title: "DUNS Number", details: supplierDetail?.dunsNumber },
        {
            title: "Business Number",
            details: supplierDetail?.businessNumber || supplierDetail?.registrationNumber,
        },
        { title: "Business Number Type", details: supplierDetail?.businessNumberType },
        { title: "Legal Form", details: supplierDetail?.legalForm },
        { title: "Line of Business", details: supplierDetail?.lineOfBusiness || supplierDetail?.subType },
        { title: "Location Status", details: supplierDetail?.locationStatus },
        { title: "Industries", details: industries },
        { title: "Brands", details: supplierDetail?.brands },
    ];

    const financialRows = [
        { title: "SIC Code", details: supplierDetail?.sicCode },
        { title: "Local Activity Code", details: supplierDetail?.localActivityCode || supplierDetail?.activityCode },
        { title: "Statement Date", details: supplierDetail?.statementDate },
        { title: "Annual Sales", details: supplierDetail?.annualSales },
        { title: "Annual Sales (USD)", details: supplierDetail?.annualSalesUsd },
        { title: "Net Worth", details: supplierDetail?.netWorth },
        { title: "Net Worth (USD)", details: supplierDetail?.netWorthUsd },
        { title: "Net Income", details: supplierDetail?.netIncome },
        { title: "Employees Count", details: supplierDetail?.employeesCount },
        { title: "Out of Business", details: toYesNo(supplierDetail?.outOfBusiness) },
    ];

    const metadataRows = [
        { title: "Source", details: supplierDetail?.source },
        { title: "Created At", details: formatDate(dateUtils, supplierDetail?.creationDate) },
        { title: "Updated At", details: formatDate(dateUtils, supplierDetail?.lastUpdate || supplierDetail?.updateDate) },
    ];

    return (
        <View style={styles.wordSectionContent}>
            <PageReference id={TOC_ANCHORS.COMPANY_OVERVIEW} />
            <Text style={styles.wordOverviewLabel}>{t("Company Overview")}:</Text>
            <View style={styles.wordOverviewBody}>
                {supplierDetail?.description ? (
                    <HTMLContent content={supplierDetail.description} />
                ) : (
                    <Text style={styles.emptyStateText}>{t("No Data")}</Text>
                )}
            </View>

            <SectionTitle title="Basic Company Information" number="1" anchorId={TOC_ANCHORS.SECTION_1} />
            <KeyValueTable rows={mapRows(basicInfoRows)} />

            <SubSectionTitle title="Address" number="1.1." anchorId={TOC_ANCHORS.SECTION_1_1} />
            <KeyValueTable rows={mapRows(buildLocationRows(supplierDetail?.locations))} />

            <SubSectionTitle title="Contact Information" number="1.2." anchorId={TOC_ANCHORS.SECTION_1_2} />
            <KeyValueTable rows={mapRows(buildContactRows(supplierData?.contactPersons))} />

            <SubSectionTitle title="Shareholders" number="1.3." anchorId={TOC_ANCHORS.SECTION_1_3} />
            <KeyValueTable rows={mapRows(buildShareholderRows(supplierData?.shareHolders))} />

            <SubSectionTitle title="Management Team" number="1.4." anchorId={TOC_ANCHORS.SECTION_1_4} />
            <KeyValueTable rows={mapRows(buildManagementRows(supplierData?.teamManagement, dateUtils))} />

            <SubSectionTitle title="Financial Summary" number="1.5." anchorId={TOC_ANCHORS.SECTION_1_5} />
            <KeyValueTable rows={mapRows(financialRows)} />

            <SubSectionTitle title="Metadata" number="1.6." anchorId={TOC_ANCHORS.SECTION_1_6} />
            <KeyValueTable rows={mapRows(metadataRows)} />
        </View>
    );
};

export default withNamespaces()(BasicCompanyInformation);
